import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { X, Home, User, Calendar, DollarSign, AlertCircle, Check, ChevronDown, Search } from 'lucide-react';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import { listOwnerCandidates, requiresOwnerChoice } from '../../utils/applicationOwner';
import { RENTAL_STATUSES, findCurrentRental, findOverlappingRental, rentalPeriod } from '../../utils/rentalPhase';

export default function AddRentalModal({ open, onClose, associationId, onSuccess }) {
  const [activeTab, setActiveTab] = useState('apartment');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showAssociationDropdown, setShowAssociationDropdown] = useState(false);
  const [associations, setAssociations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Housing search
  const [housingUnits, setHousingUnits] = useState([]);
  // Föreningens uthyrningar — behövs vid sparandet för att se om PERIODEN krockar
  const [associationRentals, setAssociationRentals] = useState([]);
  const [housingSearchTerm, setHousingSearchTerm] = useState('');
  const [showHousingDropdown, setShowHousingDropdown] = useState(false);
  const [selectedHousing, setSelectedHousing] = useState(null);
  const [ownerCandidates, setOwnerCandidates] = useState([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState(null);
  const housingDropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    // Apartment details
    address: '',
    apartmentNumber: '',
    city: '',
    zipCode: '',
    associationId: associationId || '',
    associationName: '',
    housingId: null, // Reference to selected housing unit

    // Owner details (will be filled from housing selection)
    ownerFirstName: '',
    ownerLastName: '',
    ownerLegalEntity: '',
    ownerEmail: '',

    // Tenant details
    tenantFirstName: '',
    tenantLastName: '',
    tenantEmail: '',
    tenantPhone: '',
    tenantPersonalNumber: '',

    // Economy details
    rent: '',
    deposit: '',
    bankAccount: '',
    clearing: '',

    // Period
    fromDate: '',
    toDate: '',

    // Services
    includeHeating: false,
    includeWater: false,
    includeElectricity: false,
    includeInternet: false,
    includeParking: false,
    includeFurniture: false,

    // Reason
    reason: 'Manuellt tillagd uthyrning',
  });

  // Fetch associations if not provided
  useState(() => {
    if (!associationId) {
      fetchAssociations();
    }
  }, [associationId]);

  // Fetch housing units when associationId changes or modal opens
  useEffect(() => {
    if (open && (associationId || formData.associationId)) {
      fetchHousingUnits(associationId || formData.associationId);
    }
  }, [open, associationId, formData.associationId]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (housingDropdownRef.current && !housingDropdownRef.current.contains(event.target)) {
        setShowHousingDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedHousing(null);
      setHousingSearchTerm('');
      setShowHousingDropdown(false);
      setActiveTab('apartment');
      setErrors({});
    }
  }, [open]);

  const fetchAssociations = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'associations'));
      const assocs = [];
      snapshot.forEach(doc => {
        assocs.push({ id: doc.id, ...doc.data() });
      });
      setAssociations(assocs);
    } catch (error) {
    }
  };

  const fetchHousingUnits = async (assocId) => {
    try {
      // Fetch all housing units for the association
      const q = query(collection(db, 'housing'), where('associationId', '==', assocId));
      const snapshot = await getDocs(q);
      const units = [];
      snapshot.forEach(doc => {
        units.push({ id: doc.id, ...doc.data() });
      });

      // Uthyrningar som kan uppta en lägenhet. Statusen säger att de är
      // godkända — men inte om perioden gått ut. Det avgörs av datumen, i
      // findCurrentRental, inte här. Förut räckte FINAL_APPROVED för "uthyrd",
      // och en lägenhet vars uthyrning tagit slut förblev upptagen för evigt.
      const rentalsQuery = query(
        collection(db, 'applications'),
        where('associationId', '==', assocId),
        where('status', 'in', RENTAL_STATUSES)
      );
      const rentalsSnapshot = await getDocs(rentalsQuery);
      const rentals = rentalsSnapshot.docs.map(doc => doc.data());
      setAssociationRentals(rentals);

      const unitsWithStatus = units.map(unit => {
        const activeRental = findCurrentRental(rentals, unit);
        return {
          ...unit,
          isOccupied: !!activeRental,
          activeRental: activeRental || null
        };
      });

      setHousingUnits(unitsWithStatus);
    } catch (error) {
    }
  };

  const selectHousing = (housing) => {
    setSelectedHousing(housing);

    // Har lägenheten flera ägare får formuläret inte gissa på members[0] —
    // det är den som ska stå på avtalet som ska väljas, aktivt.
    const candidates = listOwnerCandidates(housing);
    const onlyCandidate = candidates.length === 1 ? candidates[0] : null;

    setOwnerCandidates(candidates);
    setSelectedOwnerId(onlyCandidate ? onlyCandidate.id : null);

    setFormData(prev => ({
      ...prev,
      address: housing.address || '',
      apartmentNumber: housing.apartmentNumber || '',
      city: housing.city || '',
      zipCode: housing.zipCode || '',
      // Fylls i direkt bara när det inte finns något att välja mellan
      ownerFirstName: onlyCandidate?.firstName || '',
      ownerLastName: onlyCandidate?.lastName || '',
      ownerLegalEntity: onlyCandidate?.legalEntity || '',
      ownerEmail: onlyCandidate?.email || '',
      // Also link to the housing ID for reference
      housingId: housing.id,
    }));
    setShowHousingDropdown(false);
    setHousingSearchTerm('');
    // Clear any errors for these fields
    setErrors(prev => ({
      ...prev,
      address: null,
      apartmentNumber: null,
      city: null,
      zipCode: null,
      owner: null,
    }));
  };

  const selectOwner = (candidate) => {
    setSelectedOwnerId(candidate.id);
    setFormData(prev => ({
      ...prev,
      ownerFirstName: candidate.firstName,
      ownerLastName: candidate.lastName,
      ownerLegalEntity: candidate.legalEntity,
      ownerEmail: candidate.email,
    }));
    setErrors(prev => ({ ...prev, owner: null }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const selectAssociation = (assoc) => {
    setFormData(prev => ({
      ...prev,
      associationId: assoc.id,
      associationName: assoc.name
    }));
    setShowAssociationDropdown(false);
    setSearchTerm('');
    // Fetch housing units for the selected association
    fetchHousingUnits(assoc.id);
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.address) newErrors.address = 'Adress krävs';
    if (!formData.apartmentNumber) newErrors.apartmentNumber = 'Lägenhetsnummer krävs';
    if (!formData.city) newErrors.city = 'Stad krävs';
    if (!formData.zipCode) newErrors.zipCode = 'Postnummer krävs';
    if (!formData.associationId) newErrors.associationId = 'Förening krävs';
    if (requiresOwnerChoice(selectedHousing) && selectedOwnerId === null) {
      newErrors.owner = 'Välj vilken ägare som ska stå på avtalet';
    }

    if (!formData.tenantFirstName) newErrors.tenantFirstName = 'Förnamn krävs';
    if (!formData.tenantLastName) newErrors.tenantLastName = 'Efternamn krävs';
    if (!formData.tenantEmail) newErrors.tenantEmail = 'E-post krävs';
    if (!formData.tenantPhone) newErrors.tenantPhone = 'Telefon krävs';

    if (!formData.rent) newErrors.rent = 'Hyra krävs';
    if (!formData.fromDate) newErrors.fromDate = 'Startdatum krävs';
    if (!formData.toDate) newErrors.toDate = 'Slutdatum krävs';

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.tenantEmail && !emailRegex.test(formData.tenantEmail)) {
      newErrors.tenantEmail = 'Ogiltig e-postadress';
    }

    // Number validation
    if (formData.rent && (isNaN(formData.rent) || Number(formData.rent) < 0)) {
      newErrors.rent = 'Hyra måste vara ett positivt tal';
    }
    if (formData.deposit && formData.deposit !== '' && (isNaN(formData.deposit) || Number(formData.deposit) < 0)) {
      newErrors.deposit = 'Deposition måste vara ett positivt tal';
    }

    // Date validation
    if (formData.fromDate && formData.toDate) {
      const fromDate = new Date(formData.fromDate);
      const toDate = new Date(formData.toDate);
      if (fromDate >= toDate) {
        newErrors.fromDate = 'Startdatum måste vara före slutdatum';
        newErrors.toDate = 'Slutdatum måste vara efter startdatum';
      }
    }

    // Spärra bara när PERIODEN krockar med en annan uthyrning på lägenheten. En
    // kommande uthyrning går alltså att lägga in medan en annan pågår.
    if (!newErrors.fromDate && !newErrors.toDate) {
      const clash = findOverlappingRental(
        associationRentals,
        { address: formData.address, apartmentNumber: formData.apartmentNumber, zipCode: formData.zipCode },
        { fromDate: formData.fromDate, toDate: formData.toDate }
      );
      if (clash) {
        const { start, end } = rentalPeriod(clash);
        const tenant = `${clash.tenantFirstName || ''} ${clash.tenantLastName || ''}`.trim();
        const message = `Perioden krockar med en uthyrning ${start || '?'} – ${end || 'utan slutdatum'}${tenant ? ` (${tenant})` : ''}.` +
          (end ? ` Välj ett startdatum efter ${end}.` : '');
        newErrors.fromDate = message;
        newErrors.toDate = message;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      // Switch to the tab with the first error
      if (errors.address || errors.apartmentNumber || errors.city || errors.zipCode || errors.associationId || errors.owner) {
        setActiveTab('apartment');
      } else if (errors.tenantFirstName || errors.tenantLastName || errors.tenantEmail || errors.tenantPhone) {
        setActiveTab('tenant');
      } else if (errors.rent || errors.deposit) {
        setActiveTab('economy');
      } else if (errors.fromDate || errors.toDate) {
        setActiveTab('period');
      }
      return;
    }

    setLoading(true);

    try {
      // Find association name if not set
      let associationName = formData.associationName;
      if (!associationName && formData.associationId) {
        const assoc = associations.find(a => a.id === formData.associationId);
        if (assoc) {
          associationName = assoc.name;
        }
      }

      // Create the application document
      const applicationData = {
        // Core fields
        address: formData.address,
        apartmentNumber: formData.apartmentNumber,
        city: formData.city,
        zipCode: formData.zipCode,
        associationId: formData.associationId,
        housingCooperative: associationName,

        // Tenant info
        tenantFirstName: formData.tenantFirstName,
        tenantLastName: formData.tenantLastName,
        tenantEmail: formData.tenantEmail,
        tenantPhone: formData.tenantPhone,
        tenantPersonalNumber: formData.tenantPersonalNumber || '',

        // Economy - Ensure proper conversion and saving
        rent: formData.rent ? Number(formData.rent) : 0,
        deposit: formData.deposit ? Number(formData.deposit) : 0,
        bankAccount: formData.bankAccount || '',
        clearing: formData.clearing || '',

        // Period
        fromDate: formData.fromDate,
        toDate: formData.toDate,

        // Services
        includeHeating: formData.includeHeating,
        includeWater: formData.includeWater,
        includeElectricity: formData.includeElectricity,
        includeInternet: formData.includeInternet,
        includeParking: formData.includeParking,
        includeFurniture: formData.includeFurniture,

        // Metadata
        reason: formData.reason,
        status: 'FINAL_APPROVED', // Direct approval
        step: 3, // Final step
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: auth.currentUser?.email || 'hyria_admin',
        manuallyAdded: true, // Flag to indicate manual addition

        // Owner info (from housing selection). Saknas ägaren sparas TOMT — förr
        // sparades platshållaren "Okänd"/"Ägare" som namn, och då trodde resten av
        // systemet att uthyrningen hade en ägare. Vyerna visar "Okänd ägare" själva.
        ownerFirstName: formData.ownerFirstName || '',
        ownerLastName: formData.ownerLastName || '',
        // Juridisk person har varken för- eller efternamn — utan det här fältet
        // skulle den sparas som "Okänd Ägare"
        ownerLegalEntity: formData.ownerLegalEntity || null,
        ownerEmail: formData.ownerEmail || auth.currentUser?.email || 'admin@hyria.se',
        housingId: formData.housingId || null, // Reference to the housing unit
      };

      const docRef = await addDoc(collection(db, 'applications'), applicationData);

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      setErrors({ submit: 'Kunde inte skapa uthyrning. Försök igen.' });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'apartment', name: 'Lägenhet', icon: Home },
    { id: 'tenant', name: 'Hyresgäst', icon: User },
    { id: 'economy', name: 'Ekonomi', icon: DollarSign },
    { id: 'period', name: 'Period', icon: Calendar },
  ];

  const filteredAssociations = associations.filter(assoc =>
    assoc.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredHousing = housingUnits.filter(housing => {
    const searchLower = housingSearchTerm.toLowerCase();
    return (
      (housing.address && housing.address.toLowerCase().includes(searchLower)) ||
      (housing.apartmentNumber && housing.apartmentNumber.toLowerCase().includes(searchLower)) ||
      (housing.city && housing.city.toLowerCase().includes(searchLower))
    );
  });

  return (
    <Dialog open={open} onClose={onClose} className="fixed z-50 inset-0 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-3xl mx-auto max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-[#E2EAF0] flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-[#222222] font-['Poppins']">
              Lägg till manuell uthyrning
            </h2>
            <button
              onClick={onClose}
              disabled={loading}
              className="p-2 rounded-full hover:bg-zinc-100 text-zinc-500 disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-[#626262] mt-2">
            Lägg till en befintlig uthyrning direkt i systemet
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#E2EAF0] px-6 flex-shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-[#62B8E9] text-[#62B8E9]'
                  : 'border-transparent text-[#626262] hover:text-[#222222]'
              }`}
              disabled={loading}
            >
              <tab.icon className="w-4 h-4" />
              <span className="font-medium">{tab.name}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Error message */}
          {errors.submit && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{errors.submit}</p>
            </div>
          )}

          {/* Apartment Tab */}
          {activeTab === 'apartment' && (
            <div className="space-y-4">
              {/* Housing search */}
              {(associationId || formData.associationId) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-[#222222] mb-2">
                    Sök bland befintliga lägenheter i föreningen
                  </label>
                  <div className="relative" ref={housingDropdownRef}>
                    <div className="relative">
                      <input
                        type="text"
                        value={housingSearchTerm}
                        onChange={(e) => {
                          setHousingSearchTerm(e.target.value);
                          setShowHousingDropdown(true);
                        }}
                        onFocus={() => setShowHousingDropdown(true)}
                        placeholder="Sök på adress, lägenhetsnummer eller stad..."
                        className="w-full bg-white border border-[#E2EAF0] rounded-lg px-10 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                        disabled={loading}
                      />
                      <Search className="absolute left-3 top-2.5 w-5 h-5 text-[#626262]" />
                      {selectedHousing && (
                        <button
                          onClick={() => {
                            setSelectedHousing(null);
                            setOwnerCandidates([]);
                            setSelectedOwnerId(null);
                            setFormData(prev => ({
                              ...prev,
                              address: '',
                              apartmentNumber: '',
                              city: '',
                              zipCode: '',
                              ownerFirstName: '',
                              ownerLastName: '',
                              ownerLegalEntity: '',
                              ownerEmail: '',
                            }));
                            setHousingSearchTerm('');
                            setErrors(prev => ({ ...prev, owner: null }));
                          }}
                          className="absolute right-3 top-2.5 text-[#626262] hover:text-[#222222]"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>

                    {showHousingDropdown && (housingSearchTerm || filteredHousing.length > 0) && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-[#E2EAF0] rounded-lg shadow-lg max-h-60 overflow-auto">
                        {filteredHousing.length > 0 ? (
                          <div>
                            <div className="px-3 py-2 text-xs text-[#626262] border-b border-[#E2EAF0]">
                              {filteredHousing.length} lägenhet{filteredHousing.length !== 1 ? 'er' : ''} hittade
                            </div>
                            {filteredHousing.map((housing) => (
                              <button
                                key={housing.id}
                                onClick={() => selectHousing(housing)}
                                className="w-full text-left px-3 py-3 border-b border-gray-100 last:border-b-0 hover:bg-[#F9FCFF] cursor-pointer"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="font-medium text-[#222222]">
                                      {housing.address} Lgh {housing.apartmentNumber}
                                    </div>
                                    <div className="text-sm text-[#626262]">
                                      {housing.city} {housing.zipCode}
                                    </div>
                                    {housing.members?.length > 0 && (
                                      <div className="text-xs text-[#626262] mt-1">
                                        Ägare: {housing.members.map(m => m.legalEntity || `${m.firstName || ''} ${m.lastName || ''}`.trim()).filter(Boolean).join(', ')}
                                      </div>
                                    )}
                                  </div>
                                  {housing.isOccupied && (
                                    <div className="ml-3 flex-shrink-0">
                                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                                        Pågående uthyrning
                                      </span>
                                    </div>
                                  )}
                                </div>
                                {/* Information, inte en spärr: en kommande uthyrning får läggas in
                                    medan en annan pågår. Krockar perioden stoppas det vid sparandet. */}
                                {housing.isOccupied && housing.activeRental && (
                                  <div className="mt-2 p-2 bg-amber-50 rounded text-xs text-amber-800">
                                    <p>
                                      Uthyrd {rentalPeriod(housing.activeRental).start || '?'} – {rentalPeriod(housing.activeRental).end || 'utan slutdatum'}
                                      {(housing.activeRental.tenantFirstName || housing.activeRental.tenantLastName) &&
                                        ` till ${housing.activeRental.tenantFirstName || ''} ${housing.activeRental.tenantLastName || ''}`.trimEnd()}
                                    </p>
                                    {rentalPeriod(housing.activeRental).end && (
                                      <p>En ny uthyrning kan börja tidigast dagen efter.</p>
                                    )}
                                  </div>
                                )}
                              </button>
                            ))}
                          </div>
                        ) : housingSearchTerm ? (
                          <div className="px-3 py-4 text-sm text-[#626262] text-center">
                            Ingen lägenhet hittades
                          </div>
                        ) : (
                          <div className="px-3 py-4 text-sm text-[#626262] text-center">
                            Börja skriva för att söka...
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {selectedHousing && ownerCandidates.length > 1 && (
                    <div className={`mt-3 p-3 rounded-lg border ${errors.owner ? 'bg-red-50 border-red-300' : 'bg-white border-[#E2EAF0]'}`}>
                      <p className="text-sm font-medium text-[#222222] mb-1">
                        Vilken ägare ska stå på avtalet? *
                      </p>
                      <p className="text-xs text-[#626262] mb-2">
                        Lägenheten har {ownerCandidates.length} registrerade ägare.
                      </p>
                      <div className="space-y-1">
                        {ownerCandidates.map((candidate) => (
                          <label
                            key={candidate.id}
                            className="flex items-center gap-2 text-sm text-[#222222] cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="rentalOwner"
                              value={candidate.id}
                              checked={selectedOwnerId === candidate.id}
                              onChange={() => selectOwner(candidate)}
                              disabled={loading}
                            />
                            <span>
                              {candidate.label || 'Namn saknas'}
                              {candidate.email && (
                                <span className="text-[#626262]"> · {candidate.email}</span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                      {errors.owner && (
                        <p className="text-xs text-red-600 mt-2">{errors.owner}</p>
                      )}
                    </div>
                  )}

                  {selectedHousing && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 text-green-800">
                        <Check className="w-4 h-4" />
                        <span className="text-sm font-medium">Lägenhetsinformation hämtad</span>
                      </div>
                      <p className="text-xs text-green-700 mt-1">
                        {selectedHousing.address} Lgh {selectedHousing.apartmentNumber}
                      </p>
                      {selectedOwnerId !== null && (
                        <p className="text-xs text-green-700 mt-1">
                          <strong>Ägare som kommer sparas:</strong>{' '}
                          {ownerCandidates.find(c => c.id === selectedOwnerId)?.label || 'Namn saknas'}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-1">
                    Adress *
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    className={`w-full bg-white border ${errors.address ? 'border-red-400' : 'border-[#E2EAF0]'} rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]`}
                    disabled={loading}
                  />
                  {errors.address && (
                    <p className="text-xs text-red-600 mt-1">{errors.address}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-1">
                    Lägenhetsnummer *
                  </label>
                  <input
                    type="text"
                    value={formData.apartmentNumber}
                    onChange={(e) => handleChange('apartmentNumber', e.target.value)}
                    className={`w-full bg-white border ${errors.apartmentNumber ? 'border-red-400' : 'border-[#E2EAF0]'} rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]`}
                    disabled={loading}
                  />
                  {errors.apartmentNumber && (
                    <p className="text-xs text-red-600 mt-1">{errors.apartmentNumber}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-1">
                    Stad *
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    className={`w-full bg-white border ${errors.city ? 'border-red-400' : 'border-[#E2EAF0]'} rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]`}
                    disabled={loading}
                  />
                  {errors.city && (
                    <p className="text-xs text-red-600 mt-1">{errors.city}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-1">
                    Postnummer *
                  </label>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => handleChange('zipCode', e.target.value)}
                    className={`w-full bg-white border ${errors.zipCode ? 'border-red-400' : 'border-[#E2EAF0]'} rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]`}
                    disabled={loading}
                  />
                  {errors.zipCode && (
                    <p className="text-xs text-red-600 mt-1">{errors.zipCode}</p>
                  )}
                </div>
              </div>

              {/* Association selector */}
              {!associationId && (
                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-1">
                    Bostadsrättsförening *
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowAssociationDropdown(!showAssociationDropdown)}
                      className={`w-full bg-white border ${errors.associationId ? 'border-red-400' : 'border-[#E2EAF0]'} rounded-lg px-3 py-2 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#62B8E9]`}
                      disabled={loading}
                    >
                      <span className={formData.associationName ? 'text-[#222222]' : 'text-[#626262]'}>
                        {formData.associationName || 'Välj förening...'}
                      </span>
                      <ChevronDown className="w-4 h-4 text-[#626262]" />
                    </button>

                    {showAssociationDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-[#E2EAF0] rounded-lg shadow-lg max-h-60 overflow-auto">
                        <div className="p-2 border-b border-[#E2EAF0]">
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Sök förening..."
                            className="w-full bg-gray-50 border border-[#E2EAF0] rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#62B8E9]"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {filteredAssociations.map((assoc) => (
                            <button
                              key={assoc.id}
                              onClick={() => selectAssociation(assoc)}
                              className="w-full text-left px-3 py-2 hover:bg-[#F9FCFF] text-sm text-[#222222]"
                            >
                              {assoc.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.associationId && (
                    <p className="text-xs text-red-600 mt-1">{errors.associationId}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tenant Tab */}
          {activeTab === 'tenant' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-1">
                    Förnamn *
                  </label>
                  <input
                    type="text"
                    value={formData.tenantFirstName}
                    onChange={(e) => handleChange('tenantFirstName', e.target.value)}
                    className={`w-full bg-white border ${errors.tenantFirstName ? 'border-red-400' : 'border-[#E2EAF0]'} rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]`}
                    disabled={loading}
                  />
                  {errors.tenantFirstName && (
                    <p className="text-xs text-red-600 mt-1">{errors.tenantFirstName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-1">
                    Efternamn *
                  </label>
                  <input
                    type="text"
                    value={formData.tenantLastName}
                    onChange={(e) => handleChange('tenantLastName', e.target.value)}
                    className={`w-full bg-white border ${errors.tenantLastName ? 'border-red-400' : 'border-[#E2EAF0]'} rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]`}
                    disabled={loading}
                  />
                  {errors.tenantLastName && (
                    <p className="text-xs text-red-600 mt-1">{errors.tenantLastName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-1">
                    E-post *
                  </label>
                  <input
                    type="email"
                    value={formData.tenantEmail}
                    onChange={(e) => handleChange('tenantEmail', e.target.value)}
                    className={`w-full bg-white border ${errors.tenantEmail ? 'border-red-400' : 'border-[#E2EAF0]'} rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]`}
                    disabled={loading}
                  />
                  {errors.tenantEmail && (
                    <p className="text-xs text-red-600 mt-1">{errors.tenantEmail}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-1">
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    value={formData.tenantPhone}
                    onChange={(e) => handleChange('tenantPhone', e.target.value)}
                    className={`w-full bg-white border ${errors.tenantPhone ? 'border-red-400' : 'border-[#E2EAF0]'} rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]`}
                    disabled={loading}
                  />
                  {errors.tenantPhone && (
                    <p className="text-xs text-red-600 mt-1">{errors.tenantPhone}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#222222] mb-1">
                  Personnummer (valfritt)
                </label>
                <input
                  type="text"
                  value={formData.tenantPersonalNumber}
                  onChange={(e) => handleChange('tenantPersonalNumber', e.target.value)}
                  placeholder="ÅÅÅÅMMDD-XXXX"
                  className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                  disabled={loading}
                />
              </div>
            </div>
          )}

          {/* Economy Tab */}
          {activeTab === 'economy' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-1">
                    Hyra per månad (kr) *
                  </label>
                  <input
                    type="number"
                    value={formData.rent}
                    onChange={(e) => handleChange('rent', e.target.value)}
                    className={`w-full bg-white border ${errors.rent ? 'border-red-400' : 'border-[#E2EAF0]'} rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]`}
                    disabled={loading}
                  />
                  {errors.rent && (
                    <p className="text-xs text-red-600 mt-1">{errors.rent}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-1">
                    Deposition (kr)
                  </label>
                  <input
                    type="number"
                    value={formData.deposit}
                    onChange={(e) => handleChange('deposit', e.target.value)}
                    className={`w-full bg-white border ${errors.deposit ? 'border-red-400' : 'border-[#E2EAF0]'} rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]`}
                    disabled={loading}
                  />
                  {errors.deposit && (
                    <p className="text-xs text-red-600 mt-1">{errors.deposit}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-1">
                    Bankkontonummer
                  </label>
                  <input
                    type="text"
                    value={formData.bankAccount}
                    onChange={(e) => handleChange('bankAccount', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-1">
                    Clearingnummer
                  </label>
                  <input
                    type="text"
                    value={formData.clearing}
                    onChange={(e) => handleChange('clearing', e.target.value)}
                    className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Services */}
              <div>
                <label className="block text-sm font-medium text-[#222222] mb-2">
                  Inkluderade tjänster
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'includeHeating', label: 'Värme' },
                    { key: 'includeWater', label: 'Vatten' },
                    { key: 'includeElectricity', label: 'El' },
                    { key: 'includeInternet', label: 'Internet' },
                    { key: 'includeParking', label: 'Parkering' },
                    { key: 'includeFurniture', label: 'Möblerad' },
                  ].map((service) => (
                    <label key={service.key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData[service.key]}
                        onChange={(e) => handleChange(service.key, e.target.checked)}
                        className="w-4 h-4 text-[#62B8E9] border-gray-300 rounded focus:ring-[#62B8E9]"
                        disabled={loading}
                      />
                      <span className="text-sm text-[#222222]">{service.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Period Tab */}
          {activeTab === 'period' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-1">
                    Startdatum *
                  </label>
                  <input
                    type="date"
                    value={formData.fromDate}
                    onChange={(e) => handleChange('fromDate', e.target.value)}
                    className={`w-full bg-white border ${errors.fromDate ? 'border-red-400' : 'border-[#E2EAF0]'} rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]`}
                    disabled={loading}
                  />
                  {errors.fromDate && (
                    <p className="text-xs text-red-600 mt-1">{errors.fromDate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#222222] mb-1">
                    Slutdatum *
                  </label>
                  <input
                    type="date"
                    value={formData.toDate}
                    onChange={(e) => handleChange('toDate', e.target.value)}
                    className={`w-full bg-white border ${errors.toDate ? 'border-red-400' : 'border-[#E2EAF0]'} rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9]`}
                    disabled={loading}
                  />
                  {errors.toDate && (
                    <p className="text-xs text-red-600 mt-1">{errors.toDate}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#222222] mb-1">
                  Anledning / Anteckningar
                </label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => handleChange('reason', e.target.value)}
                  className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#62B8E9] resize-none"
                  rows={4}
                  disabled={loading}
                  placeholder="T.ex. befintlig uthyrning som lagts till i systemet..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#E2EAF0] flex-shrink-0">
          <div className="flex items-center justify-between">
            <p className="text-xs text-[#626262]">
              * Obligatoriska fält
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2.5 border border-[#E2EAF0] text-[#626262] rounded-lg font-semibold hover:bg-[#F9FCFF] transition-colors disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-4 py-2.5 bg-[#62B8E9] text-white rounded-lg font-semibold hover:bg-[#4A9FD1] transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Skapar...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Skapa uthyrning
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}